import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DonorFormValue } from '../../types/donation-flow.models';
import { TranslateModule } from '@ngx-translate/core';
import { PhoneInputComponent } from '../shared/form-controls/phone-input/phone-input.component';

import countriesData from 'src/assets/countries/country_states.json';
import { SelectInputComponent } from '../shared/form-controls/select/select-input.component';

type CountryData = {
  name: string;
  code: string;
  dial_code: string;
  flag: string;
  states: string[];
};

@Component({
    selector: 'don-donor-form',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        PhoneInputComponent,
        SelectInputComponent,
        TranslateModule,
    ],
    templateUrl: './donor-form.component.html',
    styleUrls: ['./donor-form.component.scss']
})
export class DonorFormComponent {
  form!: FormGroup;

  /** True once the user has attempted to submit the form at least once.
   *  Used to reveal validation errors even for fields the user never
   *  blurred (e.g. left invalid after an autofill or a patched value). */
  submitted = false;

  private pendingFormValue: DonorFormValue | null = null;

  @Input() formValue!: DonorFormValue | null;
  @Input() showBackButton = false;
  @Input() backButtonLabel = '';

  @Output() formSubmitted = new EventEmitter<DonorFormValue>();
  @Output() backClicked = new EventEmitter<void>();

  allCountries: CountryData[] = countriesData;

  mappedCountries = this.allCountries.map((c) => ({
    code: c.code,
    dialCode: c.dial_code,
    flag: c.flag,
    name: c.name,
  }));

  countries = this.allCountries.map((c) => ({
    code: c.code,
    name: c.name,
    states: c.states ?? [],
  }));
  states: string[] = [];

  countryOptions: { label: string; value: string }[] = [];
  stateOptions: { label: string; value: string }[] = [];

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      email: ['', [Validators.email, Validators.required]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      displayName: [true],
      phone: [
        '',
        [
          Validators.required,
          // Validators.minLength(8),
          // Validators.maxLength(15),
          // Validators.pattern(/^\+?\d{8,15}$/),
        ],
      ],
      isCompany: [false],
      companyName: [''],
      line1: ['', Validators.required],
      line2: [''],
      city: ['', Validators.required],
      state: [{ value: '', disabled: true }, Validators.required],
      postalCode: ['', Validators.required],
      country: ['', Validators.required],
    });

    this.form.get('isCompany')?.valueChanges.subscribe((isCo: boolean) => {
      const cn = this.form.get('companyName');
      if (isCo) {
        cn?.setValidators([Validators.required]);
      } else {
        cn?.clearValidators();
        cn?.setValue('');
      }
      cn?.updateValueAndValidity({ emitEvent: false });
    });

    this.form.get('country')?.valueChanges.subscribe((countryCode: string) => {
      this.onCountryChange(countryCode);
    });

    this.countryOptions = this.countries.map((c) => ({
      label: c.name,
      value: c.code,
    }));

    // Si el valor llegó antes de ngOnInit
    if (this.pendingFormValue) {
      this.applyFormValue(this.pendingFormValue);
    }

    void this.hydrateCountryFromGeoLookup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log({ formValue: this.formValue });
    if (changes['formValue']?.currentValue) {
      const v = changes['formValue'].currentValue as DonorFormValue;
      // Si el formulario aún no está creado, guardamos el valor
      if (!this.form) {
        this.pendingFormValue = v;
      } else {
        this.applyFormValue(v);
      }
    }
  }

  private applyFormValue(v: DonorFormValue): void {
    let countryCode = v.country?.toUpperCase() ?? '';

    const countryMatch = this.countries.find(
      (c) => c.name.toLowerCase() === v.country?.toLowerCase()
    );
    if (countryMatch) {
      countryCode = countryMatch.code;
    }

    const normalized: DonorFormValue = {
      ...v,
      country: countryCode,
      displayName: true,
    };

    this.loadStatesForCountry(countryCode);
    this.updateStateControl(countryCode);

    if (!this.states.includes(normalized.state)) {
      normalized.state = '';
    }

    this.form.patchValue(normalized, { emitEvent: false });

    if (normalized.isCompany) {
      const cn = this.form.get('companyName');
      cn?.setValidators([Validators.required]);
      cn?.updateValueAndValidity({ emitEvent: false });
    }
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    type DonorFormRaw = DonorFormValue & { phoneCode?: string };
    const raw = this.form.getRawValue() as DonorFormRaw;
    const phoneE164 = this.toE164(raw.phone, raw.phoneCode);
    const payload: DonorFormValue = { ...raw, phone: phoneE164 };
    delete (payload as any).phoneCode;

    this.formSubmitted.emit(payload);
  }

  onBackClick(): void {
    this.backClicked.emit();
  }

  /** Returns the translation key for the current validation error of a
   *  control, or null if the control is valid or hasn't been touched yet. */
  getError(controlName: string): string | null {
    const control = this.form.get(controlName);
    if (!control || !control.errors) return null;
    if (!(control.touched || this.submitted)) return null;

    if (control.errors['required']) return 'CAMPAIGN.Inputs.Errors.Required';
    if (control.errors['email']) return 'CAMPAIGN.Inputs.Errors.InvalidEmail';
    return 'CAMPAIGN.Inputs.Errors.Invalid';
  }

  onCountryChange(countryCode: string): void {
    this.loadStatesForCountry(countryCode);
    this.updateStateControl(countryCode);

    const currentState = this.form.get('state')?.value;
    if (!this.states.includes(currentState)) {
      this.form.get('state')?.setValue('', { emitEvent: false });
    }
  }

  private loadStatesForCountry(countryCode: string): void {
    const selected = this.countries.find((c) => c.code === countryCode);
    this.states = selected?.states ?? [];
    this.stateOptions = this.states.map((s) => ({ label: s, value: s }));
  }

  private updateStateControl(countryCode: string): void {
    const stateControl = this.form.get('state');
    if (!stateControl) {
      return;
    }

    if (countryCode) {
      stateControl.enable({ emitEvent: false });
      return;
    }

    stateControl.disable({ emitEvent: false });
    stateControl.setValue('', { emitEvent: false });
  }

  private async hydrateCountryFromGeoLookup(): Promise<void> {
    if (!this.form || this.form.get('country')?.value || typeof fetch === 'undefined') {
      return;
    }

    const detectedCountry =
      (await this.fetchCountryFromIp()) ?? this.detectCountryFromLocale();

    if (!detectedCountry || !this.countries.some((c) => c.code === detectedCountry)) {
      return;
    }

    if (this.form.get('country')?.value) {
      return;
    }

    this.form.get('country')?.setValue(detectedCountry);
  }

  private async fetchCountryFromIp(): Promise<string | null> {
    try {
      const response = await fetch('https://ipapi.co/json/');
      if (!response.ok) {
        return null;
      }

      const location = (await response.json()) as { country?: string };
      return location.country?.toUpperCase() ?? null;
    } catch {
      return null;
    }
  }

  private detectCountryFromLocale(): string | null {
    if (typeof navigator === 'undefined') {
      return null;
    }

    const locales = [...(navigator.languages ?? []), navigator.language].filter(
      (locale): locale is string => !!locale
    );

    for (const locale of locales) {
      const region = this.extractRegionFromLocale(locale);
      if (region && this.countries.some((c) => c.code === region)) {
        return region;
      }
    }

    return null;
  }

  private extractRegionFromLocale(locale: string): string | null {
    try {
      const intlLocale = new Intl.Locale(locale);
      const region = intlLocale.region?.toUpperCase();
      if (region) {
        return region;
      }
    } catch {
      // Fallback below for locales Intl.Locale cannot parse.
    }

    const parts = locale.split(/[-_]/).map((part) => part.trim());
    const regionPart = parts.slice(1).find((part) => /^[A-Za-z]{2}$/.test(part));

    return regionPart ? regionPart.toUpperCase() : null;
  }

  private toE164(phone: string | number, phoneCode?: string): string {
    const digits = String(phone ?? '').replace(/\D+/g, '');
    const dial = String(phoneCode ?? '').trim();
    const dialNorm = dial ? (dial.startsWith('+') ? dial : `+${dial}`) : '';
    return digits ? (dialNorm ? `${dialNorm}${digits}` : `+${digits}`) : '';
  }
}
